import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../common/hooks/hooks";
import { useGetCategoriesQuery } from "../../../api/productTaxonomyApi";
import { replaceCategories, clearCategories } from "../../../productSlice";
import { selectCategoryTree } from "../../../productSelectors";
import type { HierarchicalCategory } from "../../../productSelectors";
import {
  findCategoryInTree,
  getDescendantIds,
} from "../../../helpers/categoryTreeHelpers";

const CategoryItem: React.FC<{
  category: HierarchicalCategory;
  selectedCategories: number[];
  onCategoryClick: (id: number) => void;
  idPrefix: string;
}> = ({ category, selectedCategories, onCategoryClick, idPrefix }) => {
  return (
    <li className="filter-widget__item">
      <label
        htmlFor={`${idPrefix}category-filter-${category.id}`}
        className={`filter-widget__label ${
          selectedCategories.includes(category.id) ? "is-active" : ""
        }`}
      >
        <input
          type="checkbox"
          id={`${idPrefix}category-filter-${category.id}`}
          className="filter-widget__checkbox"
          checked={selectedCategories.includes(category.id)}
          onChange={() => onCategoryClick(category.id)}
        />
        <span className="filter-widget__name">{category.name}</span>
        <span className="filter-widget__count">{category.count}</span>
      </label>
      {category.children.length > 0 && (
        <ul className="filter-widget__sub-list">
          {category.children.map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              selectedCategories={selectedCategories}
              onCategoryClick={onCategoryClick}
              idPrefix={idPrefix}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const CategoryFilter: React.FC<{ idPrefix: string }> = ({ idPrefix }) => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // --- Data Fetching & State Management ---
  // We still call the query hook to ensure the data is fetched and in the cache.
  // Renamed `isLoading` and `isError` to be more specific.
  const { isLoading: isCategoriesLoading, isError: isCategoriesError } =
    useGetCategoriesQuery();

  // Create a centralized loading state. The component is considered "loading"
  // if either the category data is fetching OR the translation files are not ready yet.
  const isComponentLoading = isCategoriesLoading || !areTranslationsReady;

  // We use the selector to get the transformed, memoized tree.
  const categoryTree = useAppSelector(selectCategoryTree);
  const selectedCategories = useAppSelector(
    (state) => state.product.filters.category
  );

  const needsCategoryScrolling = categoryTree.length > 2;

  const handleCategoryClick = (clickedCategoryId: number) => {
    // Create a mutable copy of the currently selected categories from Redux state.
    let newSelectedCategories = [...selectedCategories];

    // Find the full object for the clicked category within our hierarchical tree.
    // This gives us access to its `parent` ID and `children`.
    const clickedCategory = findCategoryInTree(clickedCategoryId, categoryTree);

    // If, for some reason, the category isn't in our tree, do nothing.
    if (!clickedCategory) return;

    // Check if the category being clicked is already in the selected list.
    const isCurrentlySelected =
      newSelectedCategories.includes(clickedCategoryId);

    if (isCurrentlySelected) {
      // --- LOGIC FOR DESELECTING ---
      // If the category is already selected, the user wants to deselect it.
      // We simply filter it out of the array.
      newSelectedCategories = newSelectedCategories.filter(
        (id) => id !== clickedCategoryId
      );
    } else {
      // --- LOGIC FOR SELECTING ---
      // The user is selecting a new category. We need to apply our hierarchical rules.

      // RULE 1: If you select a child, deselect its parent.
      // This prevents having both "Clothing" and "T-shirts" selected.
      const parentId = clickedCategory.parent;
      if (parentId !== 0) {
        // Top-level categories have a parent ID of 0.
        // Filter out the parent ID from our new array if it exists.
        newSelectedCategories = newSelectedCategories.filter(
          (id) => id !== parentId
        );
      }

      // RULE 2: If you select a parent, deselect all of its descendants.
      // This handles the case where "T-shirts" was selected, and the user now clicks "Clothing".
      const descendantIds = getDescendantIds(clickedCategory);
      if (descendantIds.length > 0) {
        // Filter out any ID that is found in the list of descendants.
        newSelectedCategories = newSelectedCategories.filter(
          (id) => !descendantIds.includes(id)
        );
      }

      // After applying the rules, add the newly clicked category to our selection.
      newSelectedCategories.push(clickedCategoryId);
    }

    // Dispatch a single action to Redux with the final, calculated array of selected categories.
    // The `replace` action will overwrite the existing array in the slice.
    dispatch(replaceCategories(newSelectedCategories));
  };

  const handleClearCategories = () => {
    dispatch(clearCategories());
  };

  // If loading is finished, there's no error, and there are no categories to show, render nothing.
  if (!isComponentLoading && !isCategoriesError && categoryTree.length === 0) {
    return null;
  }

  return (
    <div className="filter-widget">
      {/* State 1: Centralized Loading State -> Show Skeleton */}
      {isComponentLoading && (
        <ul className="filter-widget__list">
          {Array.from({ length: 5 }).map((_, index) => (
            <li key={index} className="filter-widget__item">
              <Skeleton height={24} />
            </li>
          ))}
        </ul>
      )}

      {/* State 2: isFetching state is intentionally ignored to not cause UI flicker during background refetches,
       since taxonomy data rarely change. */}

      {/* State 3: API Error (will only show if there's an error and not loading) */}
      {!isComponentLoading && isCategoriesError && (
        <p>{t("productsArchive.sidebar.categoryError")}</p>
      )}

      {/* State 4: (No Categories) is handled by returning null above */}

      {/* State 5: Success with categories (only shown when not loading and no error) */}
      {!isComponentLoading && !isCategoriesError && categoryTree.length > 0 && (
        <>
          {/* The "All Categories" button is always outside the scroll area */}
          <ul className="filter-widget__list">
            <li className="filter-widget__item">
              <button
                className={`filter-widget__button ${
                  selectedCategories.length === 0 ? "is-active" : ""
                }`}
                onClick={handleClearCategories}
              >
                {t("productsArchive.sidebar.allCategories")}
              </button>
            </li>
          </ul>

          {/* --- Scroll Container --- */}
          <div
            className={`filter-widget__list-wrapper ${
              needsCategoryScrolling ? "is-scrollable" : ""
            }`}
          >
            <ul className="filter-widget__list">
              {categoryTree.map((category) => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  selectedCategories={selectedCategories}
                  onCategoryClick={handleCategoryClick}
                  idPrefix={idPrefix}
                />
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryFilter;
